import { useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import AssistantConsentModal, { AssistantConsentEntryPoint, toReferrer } from 'src/components/AzarOnly/AssistantConsent/AssistantConsentModal';
import SpeechRecognitionPermissionModal from 'src/components/AzarOnly/AssistantConsent/SpeechRecognitionPermissionModal';
import STTDeniedGuideForiOS from 'src/components/AzarOnly/AssistantConsent/STTDeniedGuideForiOS';
import STTDeniedGuideForMacSafari from 'src/components/AzarOnly/AssistantConsent/STTDeniedGuideForMacSafari';
import { getSttPermissionAcknowledged, requestSpeechRecognitionPermission } from 'src/components/AzarOnly/AssistantConsent/utils';
import { useClientControlPopup } from 'src/hooks/useClientControlPopup';
import useEnableRemoteConfig from 'src/hooks/useEnableRemoteConfig';
import useLogin from 'src/hooks/useLogin';
import { remoteConfigAtom } from 'src/stores/auth/atoms';
import { showModalAtom } from 'src/stores/modal/atoms';
import { featureSettingsAtom, getFeatureSettingsAtom } from 'src/stores/user/atom';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { isSafariOrIOSBrowser } from 'src/utils/device/info';
import getDeviceInfo from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
import { OrderedModalHookType } from './types';
import withLoginUserModalPromise from './withLoginUserModalPromise';
const POPUP_ID = 'ASSISTANT_CONSENT';
const useAssistantConsentModal: OrderedModalHookType = modalPropsPromise => {
  const {
    isLogin,
    isUserJustSignedUp
  } = useLogin();
  const enableAssistant = useEnableRemoteConfig('enableAssistant');
  const showOnboardingConsent = useEnableRemoteConfig('showOnboardingAssistantConsent');
  const remoteConfig = useAtomValue(remoteConfigAtom);
  const featureSettings = useAtomValue(featureSettingsAtom);
  const getFeatureSettings = useSetAtom(getFeatureSettingsAtom);
  const showModal = useSetAtom(showModalAtom);
  const {
    setDisplayTime,
    shouldDisplayPopup
  } = useClientControlPopup();
  const deviceSttReportedRef = useRef(false);
  const {
    isMobile,
    isWebKit
  } = getDeviceInfo();
  const isMobileIOS = isMobile && isWebKit;
  const showAssistantAllowPopMinutes = remoteConfig?.showAssistantAllowPop ? Number(remoteConfig.showAssistantAllowPop) : null;
  useEffect(() => {
    if (isLogin) {
      getFeatureSettings().catch(() => {
        modalPropsPromise?.resolve(null);
      });
    }
  }, [isLogin, getFeatureSettings, modalPropsPromise]);
  useEffect(() => {
    if (!isLogin || !featureSettings || deviceSttReportedRef.current) return;
    deviceSttReportedRef.current = true;
    const consented = featureSettings.find(s => s.name === 'ASSISTANT_CONSENTED')?.value;
    const enabled = featureSettings.find(s => s.name === 'ASSISTANT_ENABLED')?.value;
    const sttPermissionGranted = isSafariOrIOSBrowser() || getSttPermissionAcknowledged() === true;
    const sttEnabled = consented === true && enabled === true && sttPermissionGranted;
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ANYWHERE__REPORT_DEVICE_STT_STATUS,
      eventParams: {
        action_category: 'report',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'device_stt_status',
        status: String(sttEnabled)
      }
    });
  }, [isLogin, featureSettings]);
  const shouldDisplayAssistantModal = () => {
    const isBasicReady = isLogin && enableAssistant && featureSettings;
    if (!isBasicReady) return false;

    // assistant 이미 동의한 상태면 모달 노출 안 함
    const isAlreadyConsented = featureSettings.some(s => s.name === 'ASSISTANT_CONSENTED' && s.value === true);
    if (isAlreadyConsented) return false;

    // 신규 가입자: 온보딩 동의 팝업 노출 flag가 꺼져있으면 노출 안 함
    if (isUserJustSignedUp) return showOnboardingConsent;

    // 기존 유저: showAssistantAllowPop(분 단위 노출 주기)이 0이면 노출 비활성화
    const isDisplayEnabled = showAssistantAllowPopMinutes !== 0;
    // 기존 유저: 마지막 노출로부터 설정된 주기가 지나지 않았으면 노출 안 함
    const isCoolDownOver = shouldDisplayPopup(POPUP_ID, showAssistantAllowPopMinutes);
    return isDisplayEnabled && isCoolDownOver;
  };
  useEffect(() => {
    if (!isLogin || !enableAssistant || !featureSettings) {
      if (isLogin && !enableAssistant) {
        modalPropsPromise?.resolve(null);
      }
      return;
    }
    if (!shouldDisplayAssistantModal()) {
      modalPropsPromise?.resolve(null);
      return;
    }
    if (!isUserJustSignedUp) {
      setDisplayTime(POPUP_ID);
    }
    const entryPoint: AssistantConsentEntryPoint = isUserJustSignedUp ? 'signup' : 'mirror';
    const referrer = toReferrer(entryPoint);
    const handleConsented = async () => {
      if (isSafariOrIOSBrowser()) {
        try {
          await requestSpeechRecognitionPermission();
        } catch {
          showModal({
            key: ModalType.SPEECH_RECOGNITION_DENIED_GUIDE,
            component: () => isMobileIOS ? <STTDeniedGuideForiOS referrer={referrer} /> : <STTDeniedGuideForMacSafari referrer={referrer} />
          });
        }
      } else if (getSttPermissionAcknowledged() === null) {
        showModal({
          key: ModalType.SPEECH_RECOGNITION_PERMISSION,
          component: () => <SpeechRecognitionPermissionModal referrer={referrer} />
        });
      }
    };
    modalPropsPromise?.resolve([{
      modalProps: {
        key: ModalType.ASSISTANT_CONSENT,
        component: () => <AssistantConsentModal entryPoint={entryPoint} onConsented={handleConsented} />
      }
    }]);
  }, [isLogin, enableAssistant, isUserJustSignedUp, showOnboardingConsent, featureSettings, showAssistantAllowPopMinutes, shouldDisplayPopup, setDisplayTime, modalPropsPromise, showModal, isMobileIOS]);
};
export default withLoginUserModalPromise(useAssistantConsentModal);